import { Component, ViewChild, OnInit, ChangeDetectorRef, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModalConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { merge, cloneDeep } from 'lodash';

import {
  DocumentMainTab,
  DynamicTemplate,
  DynamicTemplateData,
  DynamicTemplateThemeEnum,
  DynamicTemplateVariable,
  DynamicDocumentFieldsTypes,
  SignerInput,
  User,
  GraphQLResponse,
  InlineFieldContext
} from '@app/models';
import { AppService, DynamicDocumentService, ErrorHandlerService, UserService, WhitelabelService } from '@app/services';

import { AlertModalService } from '@app/shared';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDocumentViewerComponent } from '../../shared/dynamic-document-viewer/dynamic-document-viewer.component';
import { untilDestroyed } from '@app/core';

@Component({
  selector: 'app-fill-dynamic-template',
  templateUrl: './fill-dynamic-template.component.html',
  styleUrl: './fill-dynamic-template.component.scss',
  providers: [NgbModalConfig, NgbModal],
  standalone: false
})
export class FillDynamicTemplateComponent implements OnInit, OnDestroy {
  currentUser: User;

  isMobile: boolean;
  isRendered = false;
  isLoading = false;
  isEditing = false;
  canSubmit = true;

  dynamicTemplate: DynamicTemplate;
  renderedDocumentScale = 1;

  fieldsToFill?: DynamicTemplateVariable[];

  private navigationState: { [k: string]: any };

  readonly DynamicDocumentFieldsTypes = DynamicDocumentFieldsTypes;
  readonly InlineFieldContext = InlineFieldContext;
  @ViewChild('renderedTemplate', { static: false }) renderedTemplate: DynamicDocumentViewerComponent;

  constructor(
    public appService: AppService,
    public whitelabelService: WhitelabelService,
    private errorHandlerService: ErrorHandlerService,
    private dynamicDocumentService: DynamicDocumentService,
    private activetedRoute: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private alertModalService: AlertModalService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.navigationState = router.getCurrentNavigation().extras.state || {};
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;

    this.userService
      .watchCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: user => (this.currentUser = user)
      });

    const templateId = this.activetedRoute.snapshot.paramMap.get('id');

    this.isLoading = true;
    this.dynamicDocumentService
      .template({ id: templateId })
      .pipe(
        tap((template: DynamicTemplate) => {
          this.dynamicTemplate = {
            name: template.name,
            description: template.description,
            keywords: template.keywords,
            category: template.category,
            is_private: template.is_private,
            lang: template.lang || this.appService.getLanguageEnumFromString(this.currentUser.locale.language),
            theme: template.theme || DynamicTemplateThemeEnum.Modern,
            created_at: template.created_at,
            updated_at: template.updated_at,
            user: template.user,
            id: template.id,

            path: '',
            signers: null,
            variables: null
          };
        }),
        switchMap((template: DynamicTemplate) => this.dynamicDocumentService.getDynamicDocumentData(template.path)),
        tap((fileData: DynamicTemplateData) => {
          this.dynamicTemplate.content = fileData.template.content || '';
          this.dynamicTemplate.signers = fileData.signers as SignerInput[];
          this.dynamicTemplate.variables = fileData.variables;

          const creatorFields = fileData.variables.filter((variable: DynamicTemplateVariable) => variable.data_signer_id === 0);
          this.fieldsToFill = this.appService.removeDuplicateFields(creatorFields);
        }),
        finalize(() => {
          this.isRendered = true;
          this.isLoading = false;
        })
      )
      .subscribe({
        error: (error: GraphQLResponse) => {
          this.errorHandlerService.handle(error);
          this.router.navigate(['/'], { replaceUrl: true });
        }
      });
  }

  ngOnDestroy() {}

  updateRenderedDocumentScale() {
    this.renderedDocumentScale = this.appService.scaleRenderedDocumentToFit(this.renderedTemplate?.editorRef.nativeElement, this.isMobile, { widthFix: 384, margin: 60 });
    this.changeDetectorRef.detectChanges();
  }

  editTemplate() {
    this.router.navigate([`dinamicos/${this.dynamicTemplate.id}/editar`], { state: { from: 'fill' } });
  }

  deleteTemplate() {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteTemplate'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        tap(() => (this.isLoading = true)),
        switchMap(() => this.dynamicDocumentService.delete({ id: this.dynamicTemplate.id })),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => this.router.navigate([`documentos/novo`]),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  submitFilledFields(result: Record<string, string>) {
    this.renderedTemplate.fillDocumentFieldsWithValues(result);

    let content = this.renderedTemplate.editor.getHTML();
    const dom = this.dynamicDocumentService.getDomForDynamicDocumentFile(content, this.dynamicTemplate.theme);
    const file = new File([new Blob([dom], { type: 'text/html' })], `${this.dynamicTemplate.name.replace(/[\.\\\/|\s]+/g, ' ')}.html`, { type: 'text/html' });

    this.router.navigate(['documentos/novo'], {
      state: merge(cloneDeep(this.navigationState), {
        file,
        isDynamicTemplate: true,
        templateTheme: this.dynamicTemplate.theme,
        variables: this.dynamicTemplate.variables,
        signers: this.dynamicTemplate.signers,
        templateHTML: dom,
        templateLang: this.dynamicTemplate.lang
      })
    });
  }

  goBack() {
    this.router.navigate(['documentos/novo'], { state: { currentTab: DocumentMainTab.TemplateUpload } });
  }

  isWhitelabel() {
    return !this.appService.isAutentiqueDomain() && !!this.whitelabelService?.whitelabel?.domain;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateRenderedDocumentScale();
  }
}
